import { useFrame } from '@react-three/fiber';
import { useRef, type ReactNode } from 'react';
import { Group } from 'three';

import Backdrop from '@cartwhl/3d/r3f/components/Backdrop';
import ContactShadow from '@cartwhl/3d/r3f/components/ContactShadow';
import Grid from '@cartwhl/3d/r3f/components/Grid';
import GroundPlane from '@cartwhl/3d/r3f/components/GroundPlane';
import Lights from '@cartwhl/3d/r3f/components/Lights';

import { usePlayerFramingMetricsRef } from '../hooks/playerFramingMetricsContext';
import { usePlayerSettingsContext } from '../hooks/playerSettingsContext';

/** Default backdrop `radius` in `@cartwhl/3d/r3f/components/Backdrop`. */
const BACKDROP_BASE_RADIUS = 500;

function EnvironmentSubjectScale({ children }: { children: ReactNode }) {
  const groupRef = useRef<Group>(null);
  const framingMetricsRef = usePlayerFramingMetricsRef();

  useFrame(() => {
    if (!groupRef.current || !framingMetricsRef) {
      return;
    }
    const { distance, sphereRadius } = framingMetricsRef.current;
    const effectiveBackdropRadius = Math.max(
      BACKDROP_BASE_RADIUS,
      distance + sphereRadius * 4,
    );
    const s = effectiveBackdropRadius / BACKDROP_BASE_RADIUS;
    groupRef.current.scale.setScalar(s);
  });

  return <group ref={groupRef}>{children}</group>;
}

export interface PlayerEnvironmentProps {
  useBackgroundGradient?: boolean;
  backdropHexColor?: string;
  floor?: 'none' | 'shadow' | 'ground';
  showGrid?: boolean;
  gridCellSize?: number;
  gridColor?: string;
}

export default function PlayerEnvironment({
  useBackgroundGradient,
  backdropHexColor,
  floor,
  showGrid,
  gridCellSize,
  gridColor,
}: PlayerEnvironmentProps) {
  const { settings } = usePlayerSettingsContext();

  const resolvedUseGradient =
    useBackgroundGradient ?? settings.environment.useBackgroundGradient;
  const resolvedBackdropColor =
    backdropHexColor ?? settings.environment.backdropHexColor;
  const resolvedFloor = floor ?? settings.environment.floor;
  const resolvedShowGrid = showGrid ?? settings.environment.showGrid;
  const resolvedGridCellSize = gridCellSize ?? settings.environment.gridCellSize;
  const resolvedGridColor = gridColor ?? settings.environment.gridColor;

  return (
    <>
      <Lights />
      <EnvironmentSubjectScale>
        <Backdrop
          useBackgroundGradient={resolvedUseGradient}
          hexColor={resolvedBackdropColor}
        />
        {resolvedFloor === 'shadow' && <ContactShadow />}
        {resolvedFloor === 'ground' && <GroundPlane />}
        {resolvedShowGrid && (
          <Grid
            cellSize={resolvedGridCellSize}
            cellColor={resolvedGridColor}
            sectionSize={resolvedGridCellSize * 5}
            sectionColor={resolvedGridColor}
          />
        )}
      </EnvironmentSubjectScale>
    </>
  );
}
